import { joyDocHelper, generateObjectId } from '@builttocreate/engine-utils';
const { getDefaultDocument, getDefaultTemplate, getDocumentFromTemplate, duplicate } = joyDocHelper;
import { applyLogic } from '@joyfill/conditional-logic';
import JoyDoc from './components/JoyDoc';
import JoyDocExporter from './components/form/exporter/JoyDocExporter';
import Navigator from './lib/navigator';
import { getDuplicatedPageAndChangelogsForDocument, getDefaultJoyDocPage } from './lib/documentHelper';
import { replaceLegacyDateTimeFormats } from './lib/dateTimeHelper';
import validator from './lib/validation';
import validateSchema from './lib/validateSchema';
import fieldOptions from './constants/DefaultFieldListOptions';
import fieldColumnTypes from './constants/FieldTableColumnTypes';

const duplicatePage = getDuplicatedPageAndChangelogsForDocument;
const getDefaultPage = getDefaultJoyDocPage;

// All functions are accessed directly from imports in the default export

/**
 * @fileoverview Joyfill Components Library - Public API Reference
 * 
 * This module exports the main components, utilities, and constants for the Joyfill form builder library.
 * It provides a comprehensive set of tools for creating, editing, and exporting dynamic forms and documents.
 */

/**
 * Main form builder component for creating and editing JoyDoc forms.
 * 
 * @class JoyDoc
 * @param {Object} props - Component props
 * @param {Object} props.doc - The JoyDoc document object containing form structure and data
 * @param {string} [props.mode='edit'] - Display mode: 'edit', 'view', or 'preview'
 * @param {string} [props.view='desktop'] - View type: 'desktop', 'mobile', or 'tablet'
 * @param {Object} [props.theme] - Custom theme object for styling
 * @param {number} [props.width=816] - Component width in pixels
 * @param {number} [props.height=1056] - Component height in pixels
 * @param {string} [props.initialPageId] - ID of the initially active page
 * @param {boolean} [props.lazyload=false] - Enable lazy loadingclea for performance
 * @param {Object} [props.features] - Feature flags object
 * @param {boolean} [props.features.formulas=false] - Enable formula support
 * @param {boolean} [props.features.readableIds=false] - Use readable IDs instead of object IDs
 * @param {boolean} [props.features.repeatingHeaderAndFooter=false] - Enable repeating headers/footers
 * @param {boolean} [props.features.validateSchema=false] - Enable schema validation
 * @param {Array} [props.fieldOptions] - Custom field options array
 * @param {Object} [props.fieldSettings] - custom settings for different targets (page, field, table column)
 * @param {Object} [props.identifiers] - Identifier configuration
 * @param {Function} [props.onChange] - Callback fired when document changes
 * @param {Function} [props.onCaptureAsync] - Async callback for field capture
 * @param {Function} [props.onUploadAsync] - Async callback for file uploads
 * @param {Function} [props.onError] - Error handler callback
 * @param {Function} [props.onFocus] - Field focus handler
 * @param {Function} [props.onBlur] - Field blur handler
 * @param {boolean} [props.validateSchema=false] - Enable schema validation
 * @returns {JSX.Element} The JoyDoc component
 * 
 * @example
 * ```jsx
 * import { JoyDoc } from '@joyfill/components';
 * 
 * <JoyDoc
 *   doc={myDocument}
 *   mode="edit"
 *   view="desktop"
 *   onChange={(updatedDoc) => console.log('Document updated:', updatedDoc)}
 *   features={{
 *     validateSchema: true,
 *     readableIds: true
 *   }}
 * />
 * ```
 */
// JoyDoc component - available via default export

/**
 * PDF export component for rendering JoyDoc forms as PDF-ready layouts.
 * 
 * @class JoyDocExporter
 * @param {Object} props - Component props
 * @param {Object} props.doc - The JoyDoc document to export
 * @param {Object} [props.theme] - Custom theme object for styling
 * @param {string} [props.license] - JWT license key for validation
 * @param {Object} [props.config] - Export configuration
 * @param {Object} [props.config.page] - Page configuration
 * @param {number} [props.config.page.height=1056] - Page height in pixels
 * @param {number} [props.config.page.width=816] - Page width in pixels
 * @param {number} [props.config.page.padding=0] - Page padding in pixels
 * @returns {JSX.Element} The JoyDocExporter component
 * 
 * @example
 * ```jsx
 * import { JoyDocExporter } from '@joyfill/components';
 * 
 * <JoyDocExporter
 *   doc={myDocument}
 *   config={{
 *     page: {
 *       height: 1056,
 *       width: 816,
 *       padding: 20
 *     }
 *   }}
 * />
 * ```
 */
// JoyDocExporter component - available via default export

/**
 * Creates a deep-cloned and properly formatted duplicate of a Template or Document.
 * 
 * Generates a new `_id`, updates the `identifier` based on type (`template_` or `doc_`),
 * resets timestamps, and merges in optional default overrides. 
 * Ensures all references to the original object are removed via deep clone.
 * 
 * @function duplicate
 * @memberof PublicAPI
 * @param {Object} original - The original Template or Document object to duplicate.
 * @param {Object} [defaults={}] - Optional default values to override or extend the duplicated object.
 * 
 * @returns {Object} A new object representing the duplicated Template or Document with updated metadata.
 * 
 * @example
 * import { duplicate } from "@joyfill/components";
 * 
 * const template = {
 *   _id: "template_123",
 *   type: "template",
 *   identifier: "template_123",
 *   name: "Inspection Template",
 *   fields: []
 * };
 * 
 * const copy = duplicate(template, { name: "Inspection Template Copy" });
 * 
 * console.log(copy);
 * // Example output:
 * // {
 * //   _id: "template_67890",
 * //   type: "template",
 * //   identifier: "template_67890",
 * //   name: "Inspection Template Copy",
 * //   fields: [],
 * //   createdOn: 1730068483105,
 * //   source: "template_123"
 * // }
 * 
 * // Explanation:
 * // - Creates a deep copy of the original object
 * // - Generates a new ObjectId and identifier
 * // - Adds a fresh `createdOn` timestamp
 * // - Stores the original identifier under `source`
 * // - Merges any provided `defaults` (e.g., new name)
 */
// duplicate function - available via default export

/**
 * Duplicates a page within a document and generates corresponding changelog entries
 * 
 * This function creates a copy of a specified page in a document and generates
 * changelog entries to track the duplication operation. It handles both primary
 * pages and view pages, as well as any associated fields that need to be created.
 * 
 * @function duplicatePage
 * @memberof PublicAPI
 * @param {Object} doc - The document object containing the page to duplicate
 * @param {string} fileId - The ID of the file containing the document
 * @param {string} pageId - The ID of the page to duplicate
 * @param {boolean} useReadableIds - Whether to use readable IDs for generated elements
 * 
 * @returns {Object} An object containing:
 *   - {Object} doc - The updated document with the duplicated page
 *   - {Array} changelogs - Array of changelog entries tracking the duplication:
 *     - Page creation changelogs for primary pages
 *     - Page creation changelogs for view pages (if any)
 *     - Field creation changelogs for any new fields created during duplication
 * 
 * @example
 * const result = getDuplicatedPage(document, 'file123', 'page456', true);
 * console.log(result.doc); // Updated document
 * console.log(result.changelogs); // Array of changelog entries
 */
// duplicatePage function - available via default export

/**
 * Creates a default page object for JoyDoc documents.
 * 
 * @function getDefaultPage
 * @memberof PublicAPI
 * @param {Object} [defaults={}] - Optional configuration to override default values
 * @returns {Object} Default page object with _id, name, width, height, rowHeight, cols, fieldPositions, layout, presentation, and padding
 * 
 * @example
 * ```javascript
 * import { getDefaultPage } from '@joyfill/components';
 * 
 * const page = getDefaultPage();
 * // Returns: { _id: "...", name: "New Page", width: 816, height: 1056, ... }
 * ```
 */
// getDefaultPage function - available via default export

/**
 * 
 * Creates a new JoyDoc document with standard properties and structure.
 * The document will have a unique ID, proper type, and identifier.
 * 
 * @function getDefaultDocument
 * @memberof PublicAPI
 * @returns {Object} returns a properly formatted Document JoyDoc
 *
 * 
 * @example
 * ```javascript
 * import { getDefaultDocument } from '@joyfill/components';
 * 
 * const newDoc = getDefaultDocument();
 * console.log('Document ID:', newDoc._id);
 * console.log('Document type:', newDoc.type);
 * console.log('Document name:', newDoc.name);
 * ```
 */
// getDefaultDocument function - available via default export

/**
 * Gets a default template structure for JoyDoc documents.
 * Creates a new template object that can be used to generate multiple documents with consistent structure.
 * Templates include predefined layouts, field configurations, and styling options.
 * 
 * @function getDefaultTemplate
 * @memberof PublicAPI
 * @returns {Object} returns a properly formatted Template JoyDoc
 * 
 * @example
 * ```javascript
 * import { getDefaultTemplate } from '@joyfill/components';
 * 
 * const template = getDefaultTemplate();
 * console.log('Template ID:', template._id);
 * console.log('Template name:', template.name);
 * ```
 */
// getDefaultTemplate function - available via default export

/**
 * Get a properly formatted Document JoyDoc from Template JoyDoc
 * 
 * Creates a new document by cloning a template and assigning it a unique ID and identifier.
 * The template is deep cloned to remove all references to the original template object.
 * The resulting document maintains the template's structure (name, files, fields) while
 * being marked as a document type with references to the source template.
 * 
 * @function getDocumentFromTemplate
 * @memberof PublicAPI
 * @param {Object} template - The template JoyDoc to create document from
 * @returns {Object} A new document with unique _id, identifier, and template references
 * 
 * @example
 * ```javascript
 * import { getDocumentFromTemplate } from '@joyfill/components';
 * 
 * const newDoc = getDocumentFromTemplate(template);
 * console.log('New document ID:', newDoc._id);
 * console.log('Document identifier:', newDoc.identifier);
 * console.log('Source template:', newDoc.template);
 * ```
 */
// getDocumentFromTemplate function - available via default export

/**
 * Evaluates and applies conditional logic to items based on defined rules and field values.
 * 
 * Each item may include a `logic` definition that determines its visibility (`hidden`)
 * based on dependent field values. The logic engine supports show/hide actions, multiple
 * condition operators, and logical grouping via AND/OR evaluation.
 * 
 * @function applyLogic
 * @memberof PublicAPI
 * @param {object[]} items - Array of items (fields, pages, or groups) that may include a `logic` object.
 * @param {object[]} fields - Array of field objects used for logic evaluation. Each field must include:
 *   - The lookup key (e.g., `_id`) matching `logic.conditions[].field`
 *   - `value` representing the current field value (string, number, or array)
 *   - `type` (optional) for informational or future logic extension.
 * @param {string} [fieldLookupKey='_id'] - Property name used to match conditions to field identifiers.
 * 
 * @returns {{ items: object[] }} 
 * Updated fields array (with `hidden` set to true or false based on the logic)
 * 
 * @description
 * Logic object structure:
 * - `action`: `'show'` or `'hide'`
 *    - `'show'`: if conditions are met, item becomes visible (`hidden = false`)
 *    - `'hide'`: if conditions are met, item becomes hidden (`hidden = true`)
 * - `eval`: `'and'` or `'or'`
 *    - `'and'`: all conditions must evaluate true
 *    - `'or'`: at least one condition must evaluate true
 * - `conditions`: array of rule objects with:
 *    - `field`: lookup key of the dependent field
 *    - `condition`: operator (see below)
 *    - `value`: value to compare against
 * 
 * Supported condition operators:
 * - `*=` → Filled (value exists or array non-empty)
 * - `null=` → Empty (null, empty string, or empty array)
 * - `=` → Equals (case-insensitive for strings; exact match for numbers)
 * - `!=` → Not equals
 * - `?=` → Contains substring (case-insensitive)
 * - `>` → Greater than (numbers only)
 * - `<` → Less than (numbers only)
 * 
 * Logic evaluation process:
 * 1. For each item with a `logic` definition:
 *    - Retrieve referenced fields using `fieldLookupKey`
 *    - Evaluate all conditions using their operator and `value`
 *    - Combine results with `eval` (`and`/`or`)
 *    - Apply `action` to update the `hidden` property accordingly
 * 2. Return updated `items` array and a `changelogs` array documenting state changes.
 * 
 * @example
 * import { applyLogic } from "@joyfill/conditional-logic";
 * 
 * const fields = [
 *   { _id: "field_one", type: "text", value: "hello world" },
 *   { _id: "field_two", type: "number", value: 100 },
 *   { _id: "field_three", type: "multiSelect", value: ["option_one"] }
 * ];
 * 
 * const items = [
 *   {
 *     _id: "conditional_one",
 *     hidden: true,
 *     logic: {
 *       action: "show",
 *       eval: "and",
 *       conditions: [
 *         { field: "field_one", condition: "?=", value: "hello" },
 *         { field: "field_two", condition: ">", value: 50 }
 *       ]
 *     }
 *   },
 *   {
 *     _id: "conditional_two",
 *     hidden: false,
 *     logic: {
 *       action: "hide",
 *       eval: "or",
 *       conditions: [
 *         { field: "field_three", condition: "=", value: "option_one" }
 *       ]
 *     }
 *   }
 * ];
 * 
 * const result = applyLogic(items, fields);
 * console.log(JSON.stringify(result, null, 2));
 * 
 * // SAMPLE OUTPUT:
 * {
 *   "items": [
 *     {
 *       "_id": "conditional_one",
 *       "hidden": false, // previously true → shown because both conditions passed
 *       "logic": { ... }
 *     },
 *     {
 *       "_id": "conditional_two",
 *       "hidden": true, // previously false → hidden because one condition passed
 *       "logic": { ... }
 *     }
 *   ],
 * 
 * }
 * 
 *  // Explanation:
 * The first item (`conditional_one`) was hidden by default but became visible.
 * Both its conditions evaluated true (`field_one` contains "hello" and `field_two` > 50).
 * Since `action: 'show'` and all conditions passed with `eval: 'and'`, it became visible.
 * 
 * The second item (`conditional_two`) was visible by default but became hidden.
 * Its condition matched because `field_three` includes "option_one".
 * Since `action: 'hide'` and the `or` condition passed, it was set to hidden.
 */
// applyLogic function - available via default export

/**
 * Generates a unique object ID for JoyDoc elements.
 * Creates a unique identifier string that can be used for documents, pages, fields,
 * and other elements in the JoyDoc system. IDs are guaranteed to be unique.
 * 
 * @function generateObjectId
 * @memberof PublicAPI
 * @returns {string} A unique object ID string (24-character hex string)
 * 
 * @example
 * ```javascript
 * import { generateObjectId } from '@joyfill/components';
 * 
 * const newId = generateObjectId();
 * console.log('New ID:', newId); // e.g., '507f1f77bcf86cd799439011'
 * 
 * // Use in document creation
 * const newDoc = {
 *   _id: generateObjectId(),
 *   title: 'My Document'
 * };
 * ```
 */
// generateObjectId function - available via default export

/**
 * Validates a JoyDoc document against field rules and constraints.
 * Performs comprehensive validation of a JoyDoc document including field validation rules,
 * required fields, data types, and business logic constraints.
 * 
 * @function validator
 * @memberof PublicAPI
 * @param {Object} doc - The JoyDoc document to validate
 * @param {Object} [options] - Validation options
 * @param {string} [options.view] - View type for validation ('desktop', 'mobile', 'tablet')
 * @returns {Object} Validation result with status and field validations
 * 
 * @example
 * ```javascript
 * import { validator } from '@joyfill/components';
 * 
 * const result = validator(doc, { view: 'desktop' });
 * if (result.status === 'valid') {
 *   console.log('Document is valid');
 * } else {
 *   console.log('Validation errors:', result.errors);
 *   result.fieldValidations.forEach(validation => {
 *     if (!validation.valid) {
 *       console.log(`Field ${validation.fieldId}: ${validation.message}`);
 *     }
 *   });
 * }
 * ```
 */
// validator function - available via default export

/**
 * Validates a JoyDoc document against the schema definition.
 * Performs schema validation to ensure the document structure conforms to the JoyDoc schema.
 * Checks for required properties, data types, and structural integrity.
 * 
 * @function validateSchema
 * @memberof PublicAPI
 * @param {Object} doc - The JoyDoc document to validate against schema
 * @returns {Object|undefined} Returns an error object if validation fails, otherwise undefined.
 *   If an error object is returned, it contains:
 *   - {string} [error.code] - Error code (e.g., 'ERROR_SCHEMA_VALIDATION')
 *   - {string} [error.message] - Human-readable error message
 *   - {Array} [error.errors] - Array of detailed validation errors
 *   - {Object} [error.details] - Additional error details including schema version
 * 
 * @example
 * ```javascript
 * import { validateSchema } from '@joyfill/components';
 * 
 * const schemaError = validateSchema(doc);
 * if (schemaError) {
 *   console.error('Schema validation failed:', schemaError.message);
 *   console.error('Error code:', schemaError.code);clear
 * } else {
 *   console.log('Document schema is valid');
 * }
 * ```
 */
// validateSchema function - available via default export

/**
 * Default field options configuration for the draggable field list.
 * Contains predefined field types, icons, and configurations that can be used
 * in the JoyDoc component to provide a standard set of available field types.
 * 
 * You can customize the draggable field options by providing your own fieldOptions array
 * with pre-configured styles, settings, and any other property supported by the JoyDoc Spec.
 * When users drag and drop pre-configured fields, they will have the properties pre-set.
 * 
 * @function fieldOptions
 * @memberof PublicAPI
 * @property {string} fieldOptions[].title - Display title for the field type
 * @property {string} fieldOptions[].type - Field type identifier (e.g., 'text', 'number', 'date', 'fieldGroup')
 * @property {string} [fieldOptions[].icon] - Icon name for the field type
 * @property {string} [fieldOptions[].iconType] - Icon type: 'custom' for SVG, 'url' for image URL
 * @property {string|ReactElement} [fieldOptions[].icon] - Custom icon (SVG element or image URL)
 * @property {string} [fieldOptions[].displayType] - Display type (e.g., 'original', 'check')
 * @property {string} [fieldOptions[].identifier] - Custom identifier for the field
 * @property {Object} [fieldOptions[].config] - Default configuration for the field type
 * @property {Array} [fieldOptions[].fields] - Nested fields for fieldGroup type
 * @property {boolean} [fieldOptions[].open] - Whether fieldGroup is open by default
 * 
 * @example
 * ```javascript
 * import { fieldOptions } from '@joyfill/components';
 * 
 * // Use default field options in JoyDoc
 * <JoyDoc fieldOptions={fieldOptions} />
 * 
 * // Custom field options with pre-configured styles
 * const customFieldOptions = [
 *   {
 *     title: 'Customer Name',
 *     type: 'text',
 *     identifier: 'custom_customer_name',
 *     fontColor: '#0096FF',
 *     fontWeight: 'bold'
 *   },
 *   {
 *     type: 'fieldGroup',
 *     title: 'Custom Fields',
 *     open: false,
 *     fields: [
 *       { title: 'Grouped Text', type: 'text' }
 *     ]
 *   }
 * ];
 * 
 * <JoyDoc fieldOptions={customFieldOptions} />
 * ```
 */
// fieldOptions constant - available via default export

/**
 * Field table column types configuration.
 * Defines available column types for table fields in JoyDoc forms.
 * These constants can be used when creating or configuring table columns
 * to ensure consistency and proper type handling.
 * 
 * @function fieldColumnTypes
 * @memberof PublicAPI
 * @property {string} text - Text column type identifier
 * @property {string} dropdown - Dropdown column type identifier
 * @property {string} image - Image column type identifier
 * @property {string} number - Number column type identifier
 * @property {string} multiSelect - Multi-select column type identifier
 * @property {string} date - Date column type identifier
 * @property {string} block - Block column type identifier
 * @property {string} barcode - Barcode column type identifier
 * @property {string} signature - Signature column type identifier
 * 
 * @example
 * ```javascript
 * import { fieldColumnTypes } from '@joyfill/components';
 * 
 * // Use in table column configuration
 * const tableColumn = {
 *   type: fieldColumnTypes.text,
 *   label: 'Name',
 *   required: true
 * };
 * 
 * // Check available types
 * console.log('Available column types:', Object.values(fieldColumnTypes));
 * ```
 */
// fieldColumnTypes constant - available via default export

export {
  JoyDoc,
  JoyDocExporter,
  Navigator,
  duplicate,
  duplicatePage,
  getDefaultPage,
  getDefaultDocument,
  getDefaultTemplate,
  getDocumentFromTemplate,
  applyLogic,
  generateObjectId,
  validator,
  validateSchema,
  fieldOptions,
  fieldColumnTypes,
  replaceLegacyDateTimeFormats,
};

/**
 * Default export object containing all public APIs of @joyfill/components.
 * 
 * @namespace PublicAPI
 * @property {Component} JoyDoc - Main form builder component
 * @property {Component} JoyDocExporter - PDF export component
 * @property {Class} Navigator - Programmatic navigation class for JoyDoc
 * @property {Function} applyLogic - Conditional logic application function
 * @property {Function} duplicate - Document duplication function
 * @property {Function} duplicatePage - Page duplication function
 * @property {Function} getDefaultPage - Default page creation function
 * @property {Function} getDefaultDocument - Default document creation function
 * @property {Function} getDefaultTemplate - Default template creation function
 * @property {Function} getDocumentFromTemplate - Document from template creation function
 * @property {Function} generateObjectId - Object ID generation function
 * @property {Function} validator - Document validation function
 * @property {Function} validateSchema - Schema validation function
 * @property {Array} fieldOptions - Default field options configuration
 * @property {Object} fieldColumnTypes - Field table column types configuration
 * 
 * 
 */
export default {
  JoyDoc,
  JoyDocExporter,
  Navigator,
  applyLogic,
  duplicate,
  duplicatePage,
  getDefaultPage,
  getDefaultDocument,
  getDefaultTemplate,
  getDocumentFromTemplate,
  generateObjectId,
  validator,
  validateSchema,
  fieldOptions,
  fieldColumnTypes,
  replaceLegacyDateTimeFormats,
};
